import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Image, Plus, Trash2, X } from "lucide-react";
import { CONTENT } from "@/constants/testIds";

import { API } from "../lib/api";
const ax = () => axios.create({ withCredentials: true });

function formatDate(dt) { try { return new Date(dt).toLocaleDateString("pt-BR"); } catch { return dt; } }

export default function GalleryPage() {
  const { hasAnyRole } = useAuth();
  const canEdit = hasAnyRole(["autor_area", "administrador_total", "admin"]);
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", content: "", media_url: "", category: "" });
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = () => ax().get(`${API}/gallery`).then(r => setItems(r.data)).catch(console.error).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await ax().post(`${API}/gallery`, form);
      setShowForm(false); load();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Remover esta foto?")) return;
    await ax().delete(`${API}/gallery/${id}`);
    load();
  };

  const categories = [...new Set(items.map(i => i.category).filter(Boolean))];

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Image className="w-5 h-5 text-[#2346ff]" />
          <div>
            <h1 className="text-xl font-bold font-heading text-slate-900">Galeria de Fotos</h1>
            <p className="text-sm text-slate-500">{items.length} fotos</p>
          </div>
        </div>
        {canEdit && (
          <Button data-testid={CONTENT.createBtn} onClick={() => setShowForm(true)} style={{ backgroundColor: "#2346ff" }} className="text-white gap-2">
            <Plus className="w-4 h-4" /> Adicionar Foto
          </Button>
        )}
      </div>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => <div key={i} className="aspect-video bg-slate-100 rounded-xl animate-pulse" />)}
        </div>
      ) : (
        <>
          {categories.length > 0 && (
            <div className="flex gap-2 mb-4 flex-wrap">
              {categories.map(c => (
                <Badge key={c} variant="secondary" className="cursor-pointer hover:bg-[#e8ecff] hover:text-[#2346ff]">{c}</Badge>
              ))}
            </div>
          )}
          <div data-testid={CONTENT.contentList} className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {items.length === 0 && (
              <div className="col-span-full text-center py-12 text-slate-400">
                <Image className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Nenhuma foto na galeria</p>
              </div>
            )}
            {items.map(item => (
              <div key={item.item_id} data-testid={CONTENT.contentItem}
                className="group relative rounded-xl overflow-hidden cursor-pointer aspect-video bg-slate-100 bento-card"
                onClick={() => setPreview(item)}>
                {item.media_url ? (
                  <img src={item.media_url} alt={item.title} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Image className="w-8 h-8 text-slate-300" />
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex flex-col justify-end p-3">
                  <p className="text-white text-xs font-semibold line-clamp-1">{item.title}</p>
                  {item.category && <p className="text-white/70 text-[10px]">{item.category}</p>}
                </div>
                {canEdit && (
                  <Button
                    variant="ghost"
                    size="icon"
                    data-testid={CONTENT.deleteBtn}
                    className="absolute top-2 right-2 h-7 w-7 bg-black/50 text-white hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-all"
                    onClick={e => { e.stopPropagation(); remove(item.item_id); }}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {/* Preview Modal */}
      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={() => setPreview(null)}>
          <div className="relative max-w-3xl w-full" onClick={e => e.stopPropagation()}>
            <Button variant="ghost" size="icon" className="absolute -top-10 right-0 text-white hover:bg-white/20" onClick={() => setPreview(null)}>
              <X className="w-5 h-5" />
            </Button>
            {preview.media_url && <img src={preview.media_url} alt={preview.title} className="w-full rounded-xl" />}
            <div className="bg-white/10 backdrop-blur rounded-b-xl p-3">
              <p className="text-white font-semibold">{preview.title}</p>
              {preview.content && <p className="text-white/70 text-sm mt-0.5">{preview.content}</p>}
              <p className="text-white/50 text-xs mt-1">{formatDate(preview.created_at)}</p>
            </div>
          </div>
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Adicionar Foto</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Título *</Label><Input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} /></div>
            <div><Label>URL da Imagem *</Label><Input value={form.media_url} onChange={e => setForm(f => ({...f, media_url: e.target.value}))} placeholder="https://..." /></div>
            {form.media_url && <img src={form.media_url} alt="preview" className="w-full h-32 object-cover rounded-lg" onError={() => {}} />}
            <div><Label>Descrição</Label><Textarea rows={2} value={form.content} onChange={e => setForm(f => ({...f, content: e.target.value}))} /></div>
            <div><Label>Categoria</Label><Input value={form.category} onChange={e => setForm(f => ({...f, category: e.target.value}))} placeholder="Ex: Eventos" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
            <Button data-testid={CONTENT.saveBtn} onClick={save} disabled={saving || !form.title || !form.media_url} style={{ backgroundColor: "#2346ff" }} className="text-white">{saving ? "Salvando..." : "Adicionar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
